import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context';
import { X, UserPlus, Star, Plus, Camera, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import ContactAvatar from './ContactAvatar';
import {
  RELATIONSHIP_TYPES,
  COMMUNICATION_TYPES,
  SUGGESTED_TAGS,
  useCommMethods,
  useTags,
  usePhotoUpload,
} from '../lib/contactFormHelpers';
import SortableCommList from './SortableCommList';

export default function AddContact() {
  const navigate = useNavigate();
  const { addContact } = useApp();

  const [name, setName] = useState('');
  const [photoUrl, setAvatar] = useState('');
  const [relationshipType, setRelationshipType] = useState('Friend');
  const [innerCircle, setInnerCircle] = useState(false);
  const [birthday, setBirthday] = useState('');
  const [yearMet, setYearMet] = useState('');
  const [contactFrequencyDays, setContactFrequencyDays] = useState<number | ''>('');
  const {
    communicationMethods,
    preferredCommMethod,
    setPreferredCommMethod,
    commType,
    setCommType,
    commValue,
    setCommValue,
    handleAddCommMethod,
    handleRemoveCommMethod,
    handleReorder,
  } = useCommMethods();

  const {
    selectedTags,
    customTag,
    setCustomTag,
    isAddingTag,
    setIsAddingTag,
    toggleTag,
    handleAddCustomTag,
  } = useTags();

  const { uploading, handleFileChange, cropModal } = usePhotoUpload(setAvatar, { type: 'none' });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const handleSubmit = () => {
    if (!name.trim()) return;

    addContact({
      name: name.trim(),
      photoUrl: photoUrl.trim() || undefined,
      status: 'new' as const,
      healthScore: -1,
      lastInteractionDate: '',
      overdueDays: 0,
      birthday: birthday || undefined,
      tags: selectedTags,
      yearMet: yearMet || undefined,
      contactFrequencyDays:
        typeof contactFrequencyDays === 'number' ? contactFrequencyDays : undefined,
      communicationMethods: communicationMethods.length > 0 ? communicationMethods : undefined,
      preferredCommMethod: preferredCommMethod || undefined,
      relationshipType,
      innerCircle,
    });

    navigate('/contacts');
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Top App Bar */}
      <div className="flex items-center bg-[#f8f5f5] p-4 pb-2 justify-between sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="text-slate-900 flex size-12 shrink-0 items-center justify-center cursor-pointer -ml-2"
        >
          <X size={24} />
        </button>
        <h2 className="text-slate-900 text-lg font-bold leading-tight tracking-tight flex-1 text-center">
          Add Contact
        </h2>
        <div className="size-12 shrink-0 border border-transparent -mr-2"></div>
      </div>

      <div className="px-6 py-4 space-y-8">
        {/* PhotoUrl Upload */}
        <div className="flex justify-center flex-col items-center gap-3">
          <label className="relative cursor-pointer group">
            <div className="size-24 rounded-full border-4 border-[#fa8585]/20 overflow-hidden relative">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt="Contact Photo"
                  className="w-full h-full object-cover transition-opacity group-hover:opacity-60"
                />
              ) : (
                <ContactAvatar
                  name={name || ''}
                  size="xl"
                  className="size-full transition-opacity group-hover:opacity-60"
                />
              )}
              {/* Overlay */}
              <div
                className={cn(
                  'absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity',
                  uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                )}
              >
                {uploading ? (
                  <Loader2 size={24} className="text-white animate-spin" />
                ) : (
                  <Camera size={24} className="text-white" />
                )}
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileChange(file);
              }}
              className="hidden"
            />
          </label>
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Add Photo
          </span>
        </div>

        {/* Name */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">Name</h3>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sarah Jenkins"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
          />
        </div>

        {/* Relationship Type */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Relationship
          </h3>
          <div className="grid grid-cols-5 gap-3">
            {RELATIONSHIP_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setRelationshipType(type.id)}
                className={cn(
                  'flex flex-col gap-2 rounded-xl p-3 items-center transition-all border-2',
                  relationshipType === type.id
                    ? 'border-[#fa8585] bg-[#fa8585]/5'
                    : 'border-slate-200 bg-white hover:border-[#fa8585]/50',
                )}
              >
                <type.icon
                  className={cn(relationshipType === type.id ? 'text-[#fa8585]' : 'text-slate-500')}
                  size={24}
                />
                <span className="text-xs font-bold text-slate-700">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Inner Circle Toggle */}
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <Star
              size={20}
              className={innerCircle ? 'text-[#fa8585] fill-[#fa8585]' : 'text-slate-400'}
            />
            <div>
              <p className="text-sm font-bold text-slate-900">Inner Circle</p>
              <p className="text-[11px] text-slate-400">People you never want to lose touch with</p>
            </div>
          </div>
          <button
            onClick={() => setInnerCircle(!innerCircle)}
            className={cn(
              'w-12 h-7 rounded-full transition-colors relative',
              innerCircle ? 'bg-[#fa8585]' : 'bg-slate-300',
            )}
          >
            <div
              className={cn(
                'absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform',
                innerCircle ? 'translate-x-5' : 'translate-x-0.5',
              )}
            />
          </button>
        </div>

        {/* Year Met */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Year Met <span className="text-slate-400 font-normal lowercase">(optional)</span>
          </h3>
          <select
            value={yearMet}
            onChange={(e) => setYearMet(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all appearance-none"
          >
            <option value="">Select a year...</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {/* Contact Frequency */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Reach out frequency{' '}
            <span className="text-slate-400 font-normal lowercase">(optional)</span>
          </h3>
          <select
            value={contactFrequencyDays}
            onChange={(e) =>
              setContactFrequencyDays(e.target.value === '' ? '' : parseInt(e.target.value))
            }
            className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all appearance-none"
          >
            <option value="">Select a frequency...</option>
            <option value={1}>Daily</option>
            <option value={7}>Weekly</option>
            <option value={14}>Bi-weekly</option>
            <option value={30}>Monthly</option>
            <option value={90}>Quarterly</option>
            <option value={180}>Bi-annually</option>
            <option value={365}>Yearly</option>
          </select>
        </div>

        {/* Communication Methods */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Ways to connect <span className="text-slate-400 font-normal lowercase">(optional)</span>
          </h3>

          {communicationMethods.length > 0 && (
            <SortableCommList
              communicationMethods={communicationMethods}
              preferredCommMethod={preferredCommMethod}
              onSetPreferred={setPreferredCommMethod}
              onRemove={handleRemoveCommMethod}
              onReorder={handleReorder}
            />
          )}

          <div className="flex gap-2">
            <select
              value={commType}
              onChange={(e) => setCommType(e.target.value)}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:ring-2 focus:ring-[#fa8585] outline-none transition-all appearance-none w-1/3"
            >
              {COMMUNICATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={commValue}
              onChange={(e) => setCommValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCommMethod()}
              placeholder="Value..."
              className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:ring-2 focus:ring-[#fa8585] outline-none transition-all"
            />
            <button
              onClick={handleAddCommMethod}
              disabled={!commValue.trim()}
              className="px-4 py-3 rounded-xl text-sm font-bold bg-slate-900 text-white disabled:opacity-50 transition-all active:scale-95"
            >
              ADD
            </button>
          </div>
        </div>

        {/* Birthday (optional) */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Birthday <span className="text-slate-400 font-normal lowercase">(optional)</span>
          </h3>
          <input
            type="date"
            value={birthday}
            onChange={(e) => setBirthday(e.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-900 focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
          />
        </div>

        {/* Tags */}
        <div>
          <h3 className="text-slate-900 text-sm font-bold uppercase tracking-widest pb-3">
            Tags <span className="text-slate-400 font-normal lowercase">(optional)</span>
          </h3>

          <div className="flex flex-wrap gap-2">
            {Array.from(new Set([...SUGGESTED_TAGS, ...selectedTags])).map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={cn(
                  'px-4 py-1.5 rounded-full text-sm font-semibold transition-all',
                  selectedTags.includes(tag)
                    ? 'bg-[#fa8585] text-white shadow-sm shadow-[#fa8585]/20'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-[#fa8585]/50',
                )}
              >
                {tag}
              </button>
            ))}

            {isAddingTag ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomTag();
                    }
                  }}
                  autoFocus
                  placeholder="tag name..."
                  className="px-4 py-1.5 w-28 rounded-full text-sm font-semibold bg-white border border-[#fa8585] text-slate-900 outline-none focus:ring-2 focus:ring-[#fa8585]/30 transition-all"
                />
                <button
                  type="button"
                  onClick={handleAddCustomTag}
                  disabled={!customTag.trim()}
                  className="px-3 py-1.5 rounded-full text-sm font-bold bg-[#fa8585] text-white disabled:opacity-50 transition-all active:scale-95 shadow-sm shadow-[#fa8585]/20"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingTag(true)}
                className="px-3 py-1.5 rounded-full text-sm font-bold bg-white border border-dashed border-slate-300 text-slate-500 hover:text-[#fa8585] hover:border-[#fa8585] transition-all flex items-center gap-1"
              >
                <Plus size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Bottom Action */}
      <div className="mt-auto p-6 bg-[#f8f5f5] border-t border-slate-200/50">
        <button
          onClick={handleSubmit}
          disabled={!name.trim()}
          className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 disabled:opacity-40 text-white font-bold py-4 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 group transition-all transform active:scale-[0.98]"
        >
          <UserPlus size={20} className="group-hover:scale-110 transition-transform" />
          <span>Add Contact</span>
        </button>
        <p className="text-center text-xs text-slate-400 mt-4">
          Stay connected with the people who matter.
        </p>
      </div>
      {cropModal}
    </div>
  );
}
